
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { LogIn, User, Key, MailQuestion, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Removed isLogin state - now only supports login
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [isResetFlow, setIsResetFlow] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  // Check for password reset flow
  useEffect(() => {
    console.log("[Auth] URL location:", location.pathname, location.search);
    
    // Check URL params for password reset flow indicators
    const query = new URLSearchParams(location.search);
    const isReset = query.get('reset') === 'true';
    const accessToken = query.get('access_token');
    const refreshToken = query.get('refresh_token');
    const type = query.get('type');
    
    // Also check for token in the URL which indicates we came from the email link
    const hasToken = location.hash.includes('access_token=') || location.hash.includes('type=recovery');
    
    if (isReset || accessToken || (type === 'recovery' && refreshToken) || hasToken) {
      console.log("[Auth] Detected password reset flow", { 
        isReset, 
        accessToken, 
        type, 
        refreshToken, 
        hasToken,
        hash: location.hash
      });
      setIsResetFlow(true);
      
      // Handle hash fragment from Supabase redirect
      if (hasToken) {
        // Attempt to extract session from URL if it exists
        supabase.auth.getSession().then(({ data }) => {
          if (data.session) {
            console.log("[Auth] Session detected from URL parameters");
          } else {
            console.log("[Auth] No session from URL, attempting to process hash");
            // This will process the hash fragment
            supabase.auth.onAuthStateChange((event, session) => {
              console.log("[Auth] Auth state change:", event, !!session);
            });
          }
        });
      }
    }
  }, [location]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isResetFlow) {
        // Handle password update for reset flow
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        
        if (error) throw error;
        
        setResetComplete(true);
        toast({
          title: "Password updated",
          description: "Your password has been successfully updated.",
        });
        
        // Clear the URL parameters
        window.history.replaceState({}, document.title, "/auth");
      } else if (isPasswordReset) {
        // Send password reset email
        // Use the exact URL format as specified in Supabase redirect URLs
        // Include https:// protocol and www. subdomain if that's what's set in Supabase
        const resetRedirectUrl = "https://www.seedpotatoorganisation.com/auth/reset-password";
        console.log(`[Auth] Sending reset link with redirect to: ${resetRedirectUrl}`);
        
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: resetRedirectUrl,
        });

        if (error) throw error;

        setResetSent(true);
        toast({
          title: "Reset link sent",
          description: "Check your email for a password reset link",
        });
      } else {
        // Login only - no signup
        const { error } = await supabase.auth.signInWithPassword({ email, password });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Logged in successfully!",
        });
        navigate("/admin");
      }
    } catch (error) {
      console.error("Authentication error:", error);
      toast({
        title: "Error",
        description: error.message || "Authentication failed",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsPasswordReset(false);
    setResetSent(false);
    setEmail("");
    setIsResetFlow(false);
    setResetComplete(false);
    // Clear URL parameters if any
    window.history.replaceState({}, document.title, "/auth");
  };

  // Show a fresh login form after password reset is complete
  if (resetComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
            <h2 className="mt-6 text-3xl font-bold text-gray-900">Password Reset Complete</h2>
            <p className="mt-2 text-gray-600">Your password has been successfully reset.</p>
          </div>
          <div className="mt-8">
            <Button 
              className="w-full" 
              onClick={resetForm}
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign in with your new password
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {isResetFlow
              ? "Reset your password"
              : isPasswordReset
              ? "Reset your password"
              : "Sign in to your account"}
          </h2>
        </div>

        {resetSent && (
          <Alert>
            <AlertDescription>
              We've sent a password reset link to your email address. Please check
              your inbox and follow the instructions to reset your password.
            </AlertDescription>
          </Alert>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleAuth}>
          <div className="space-y-4">
            {!isResetFlow && (
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    required
                    className="pl-10"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
            )}
            
            {!isPasswordReset && !isResetFlow && (
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="password"
                    type="password"
                    required={!isPasswordReset}
                    className="pl-10"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
            )}

            {isResetFlow && (
              <div>
                <label htmlFor="newPassword" className="sr-only">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="newPassword"
                    type="password"
                    required
                    className="pl-10"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Password must be at least 6 characters long.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-4">
            <Button type="submit" disabled={loading}>
              {isResetFlow ? (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {loading ? "Updating..." : "Update Password"}
                </>
              ) : isPasswordReset ? (
                <>
                  <MailQuestion className="mr-2 h-4 w-4" />
                  {loading ? "Sending..." : "Send reset link"}
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  {loading ? "Loading..." : "Sign in"}
                </>
              )}
            </Button>
            
            {/* Removed signup toggle - admin login only */}
            
            {isResetFlow || isPasswordReset ? (
              <Button
                type="button"
                variant="link"
                onClick={resetForm}
                className="text-sm"
              >
                <ArrowLeft className="mr-2 h-3 w-3" />
                Back to sign in
              </Button>
            ) : (
              <Button
                type="button"
                variant="link"
                onClick={() => setIsPasswordReset(true)}
                className="text-sm"
              >
                Forgot your password?
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Auth;
